import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ChevronRight } from "lucide-react";
import { API } from "../constants";
import { categorySlug } from "./CategoryLandingPage";

// Icon per category (Unicode-safe FontAwesome-style visual using inline emoji-free chip)
const CATEGORY_ORDER = [
  "Metal Polish", "Compounds", "Cleaners", "Waxes", "Protectants", "Dressings",
  "Interior", "Kits", "Buffing Wheels", "Goblets & Tapers",
  "Adapters & Accessories", "Accessories",
];

const CATEGORY_TAGLINES = {
  "Metal Polish": "Mirror finish. Every surface.",
  "Compounds": "Erase swirls & oxidation.",
  "Cleaners": "Prep like a pro.",
  "Waxes": "Depth, gloss, and armor.",
  "Protectants": "Ceramic-grade protection.",
  "Dressings": "Tire, trim, rubber done right.",
  "Interior": "Leather, vinyl, fabric care.",
  "Kits": "Everything you need, in one box.",
  "Buffing Wheels": "Cotton, sisal, airway — every size.",
  "Goblets & Tapers": "Reach every corner.",
  "Adapters & Accessories": "Fit it to your polisher.",
  "Accessories": "The finishing extras.",
};

const CategoriesIndexPage = () => {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/products?limit=500&in_stock=true`)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((p) => {
          const c = p.category || "Uncategorized";
          if (!map[c]) map[c] = { name: c, count: 0, cutAbove: 0, sample: [] };
          map[c].count += 1;
          if (p.has_cut_above_logo) map[c].cutAbove += 1;
          if (map[c].sample.length < 3) map[c].sample.push(p);
        });
        const ordered = CATEGORY_ORDER.filter((c) => map[c]).map((c) => map[c]);
        const rest = Object.values(map).filter((b) => !CATEGORY_ORDER.includes(b.name));
        setBuckets([...ordered, ...rest]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-24" data-testid="categories-index-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Shop By Category
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">All Categories</h1>
          <p className="text-gray-400 text-base mt-2 max-w-2xl">
            Every product we stock, grouped so you can find exactly the right formula, wheel, or accessory in seconds.
          </p>
        </div>

        {loading && <p className="text-gray-500 text-sm">Loading…</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {buckets.map((bucket, idx) => (
            <Link
              key={bucket.name}
              to={`/category/${categorySlug(bucket.name)}`}
              className="group relative overflow-hidden bg-[#0F0F0F] border border-white/5 hover:border-[#D4AF37]/50 rounded-sm transition-all duration-300 hover:-translate-y-1"
              data-testid={`category-tile-${idx}`}
            >
              <div className="relative h-40 bg-[#1A1A1A] overflow-hidden">
                {bucket.sample[0] && (
                  <img
                    src={bucket.sample[0].image_url}
                    alt={bucket.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/50 to-transparent" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-xl text-white group-hover:text-[#D4AF37] transition-colors">{bucket.name}</h3>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-gray-500 text-sm mb-3">{CATEGORY_TAGLINES[bucket.name] || "Explore the collection."}</p>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest">
                  <span className="text-gray-500"><span className="text-white font-bold text-base">{bucket.count}</span> Products</span>
                  {bucket.cutAbove > 0 && (
                    <span className="text-gray-500"><span className="text-[#D4AF37] font-bold text-base">{bucket.cutAbove}</span> Cut Above</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoriesIndexPage;
